<template>
  <div class="details-component">
    <div class="details-component__header">
      <h1>{{ $t('users.import_user') }}</h1>
    </div>

    <div class="details-component__content content" v-if="!isImported">
      <form class="form">
        <fieldset class="form__fieldset">
          <div
            class="form__field"
            v-if="isSuperAdmin()"
            :class="{'form__field_error' :errors.has('landlord')}"
          >
            <label for="landlord" class="form__label">{{ $t('common.landlord') }}</label>
            <el-select
              name="landlord"
              v-model="landlord"
              v-validate="'validLandlord'"
              :data-vv-as="$t('common.landlord')"
              v-if="landlords.items"
              :placeholder="$t('common.landlord')"
              @change="onLandlordChange"
            >
              <el-option
                v-for="landlord in landlords.items"
                :key="landlord.id"
                :label="landlord.name"
                :value="landlord.id"
              />
            </el-select>
            <span v-else-if="landlord">{{ landlord.name }}</span>
            <tooltip
              v-show="errors.has('landlord')"
              class="form__validation"
            >{{ $t('errors.landlord_field_required') }}</tooltip>
          </div>

          <div
            class="form__field"
            v-if="isLandlord()"
            :class="{'form__field_error' :errors.has('tenant')}"
          >
            <label for="tenant" class="form__label">{{ $t('common.tenant') }}</label>
        <el-select name="tenant"
                  v-model="tenant"
                  filterable
                  v-validate="'validTenant'"
                  :data-vv-as="$t('common.tenant')"
                  v-if="tenants.data"
                  :placeholder="$t('common.tenant')">
                <el-option
                  v-for="tenant in tenants.data"
                  :key="tenant.id"
                  :label="tenant.name"
                  :value="tenant.id">
                </el-option>
              </el-select>
            <span v-else-if="tenant">{{ tenant.name }}</span>
            <tooltip
              v-show="errors.has('tenant')"
              class="form__validation"
            >{{ $t('errors.tenant_field_required') }}</tooltip>
          </div>

          <div class="form__field" :class="{'form__field_error' : errors.has('separator')}">
            <label for="separator" class="form__label">{{ $t('import.column_separator') }}</label>
            <input
              type="text"
              class="form__input"
              name="separator"
              id="separator"
              v-model="form.separator"
              :data-vv-as="$t('import.column_separator')"
              data-vv-delay="1000"
            />
          </div>

          <div class="form__field" :class="{'form__field_error' : errors.has('separator')}">
            <label class="form__label form__label__long no-hide with_checkbox">
              <span class="custom-checkbox-label">{{ $t('add_tenant.managed_by_tenant') }}</span>
              <input v-model="form.managed_by_tenant" type="checkbox" id="can_use_cards" name="can_use_cards" class="form__fieldset__checkbox" >
              <span v-model="form.managed_by_tenant" class="form__fieldset__custom-checkbox"></span>
            </label>
          </div>

          <div
            class="form__field form__field--textarea"
            :class="{'form__field_error' : errors.has('data')}"
          >
            <label
              for="data"
              class="form__label"
            >{{ $t('import.import_data') }} ({{ $t('import.format') }}: <span v-if="!form.managed_by_tenant">{{ $t('common.email') }};</span>{{ $t('common.name') }};{{ $t('common.phone') }};{{ $t('cards.plate') }} ({{ $t('import.separated_by_comma') }}))</label>
            <textarea
              class="form__input"
              name="data"
              id="data"
              v-model="form.data"
              v-validate="'required'"
              :data-vv-as="$t('import.import_data')"
              data-vv-delay="1000"
              rows="5"
            />
            <tooltip
              v-show="errors.has('data')"
              class="form__validation"
            >{{ $t('errors.field_required') }}</tooltip>
          </div>
        </fieldset>
        <div class="form__buttons buttonset">
          <router-link
            :to="{ name: 'users' }"
            class="buttonset__button btn btn--secondary"
          >{{ $t('common.discard') }}</router-link>
          <button
            type="submit"
            :disabled="isButtonDisabled"
            :class="[{'btn--disabled' : isButtonDisabled }, 'buttonset__button btn btn--main ']"
            @click.prevent.stop="onSubmit()">
              <span v-if="isButtonDisabled">
                <i class="fa fa-spinner fa-spin"></i>
              </span>
              <span v-else>{{ $t('common.save') }}</span>
            </button>
        </div>
      </form>
    </div>

    <div class="details-component__content content" v-else>
      <div class="import-message">
        <p>
          {{ $t('import.residents_successfully_imported') }}:
          <strong>{{importedResponse.user_created_count}} из {{importedResponse.lines_processed_count}}</strong>.
        </p>

        <div v-if="importedResponse.lines_with_error.length">
          <p>{{ $t('import.entries_with_errors') }}:</p>
          <ul>
            <li v-for="(line, index) in importedResponse.lines_with_error" :key="index">
              {{line.email}} | {{line.name}} | {{line.mobile}} |
              <span
                v-for="vehicle in line.vehicleReg"
                :key="vehicle"
              >{{vehicle}},</span>
            </li>
          </ul>
        </div>

        <p>{{ $t('import.vehicle_created_count') }}: {{importedResponse.vehicle_created_count}}</p>

        <div class="form__buttons buttonset">
          <button
            v-if="importedResponse.lines_with_error.length"
            type="button"
            class="buttonset__button btn btn--main"
            @click="closeWithErrors"
          >{{ $t('common.close') }}</button>
          <router-link
            v-else
            :to="{ name: 'users' }"
            class="buttonset__button btn btn--main"
          >{{ $t('common.close') }}</router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import Tooltip from 'common/Tooltip';
import { parse } from 'libphonenumber-js';

export default {
  components: {
    Tooltip,
  },

  data() {
    return {
      form: {
        separator: ';',
        data: '',
      },
      landlord: null,
      tenant: null,
      isImported: false,
      importedResponse: null,
      isButtonDisabled: false,
    };
  },

  methods: {
    fetchAllData() {
      if (this.isLandlord()) {
        this.fetchTenants();
      }
      if (this.isSuperAdmin()) {
        this.fetchLandlords();
        this.onLandlordChange();
      }
    },
    onLandlordChange() {
      if (this.landlord) {
        this.fetchTenants(this.landlord);
      }
    },
    fetchLandlords() {
      if (this.user.data) {
        // Fetch landlords
        this.$store.dispatch('landlord/landlords/list', { user: this.user })
      }
    },
    fetchTenants(landlordId) {
      if (this.user.data.selectedRole.isSuperadmin && landlordId) {
        this.$store.dispatch('landlord/tenants/autocomplete', {landlordId: landlordId, user: this.user});
      } else {
        this.$store.dispatch('landlord/tenants/autocomplete', {landlordId: [this.user.data.selectedRole.id], user: this.user});
      }
    },
    resetValidators() {
      this.$validator.remove('validLandlord');
      this.$validator.remove('validTenant');
      this.$validator.clean();
    },
    createValidators() {
      this.createTenantValidator();
      this.createLandlordValidator();
    },
    createTenantValidator() {
      this.$validator.extend('validTenant', {
        getMessage: () => 'Tenant must be selected',
        validate: () => this.isLandlord() && (typeof this.tenant !== 'undefined' && this.tenant !== null),
      });
    },
    createLandlordValidator() {
      this.$validator.extend('validLandlord', {
        getMessage: () => 'Tenant must be selected',
        validate: () => this.isSuperAdmin() && (typeof this.landlord !== 'undefined' && this.landlord !== null),
      });
    },
    onSubmit() {
      this.$validator.validateAll(
        {
          data: this.form.data,
          tenant: 'validTenant',
          landlord: 'validLandlord',
        },
      )
        .then((success) => {
          if (success) {
            this.isButtonDisabled = true;
            const data = {
              created_by: this.landlord,
              partner_id: this.tenant,
              users: [...this.getParseData()],
            };
            if (this.form.managed_by_tenant) {
              data.managed_by_tenant = this.form.managed_by_tenant;
            }
            this.$store.dispatch('users/userImport/postImport', {
              user: this.user,
              data,
            })
              .then((response) => {
                this.isImported = true;
                this.importedResponse = response.data;

                if (this.importedResponse.lines_with_error &&
                this.importedResponse.lines_with_error.length > 0) {
                  this.isButtonDisabled = false;
                }
              });
          } else {
            this.isButtonDisabled = false;
          }
        });
    },
    getParseData() {
      const byNewLine = this.form.data.split('\n');
      const byTabs = byNewLine.map(line => line.split(this.form.separator));
      const collection = byTabs.map((item) => {
        const data = {};

        if (this.form.managed_by_tenant) {
          data.name = item[0];
          data.mobile = item[1];
          data.mobileCountry = parse(item[1], { country: { default: 'EE' } }).country || '';
          data.vehicleReg = item[2] && item[2].length ? item[2].split(',') : [];
        } else {
          data.email = item[0];
          data.name = item[1];
          data.mobile = item[2];
          data.mobileCountry = parse(item[2], { country: { default: 'EE' } }).country || '';
          data.vehicleReg = item[3] && item[3].length ? item[3].split(',') : [];
        }

        return data;
      });

      return collection;
    },
    getErrorsStringify() {
      const { separator } = this.form;

      const objectToStringArray = this.importedResponse.lines_with_error.map(error => (
        error.email +
        separator +
        error.name +
        separator +
        error.mobile +
        separator +
        error.vehicleReg.join(',')
      ));

      const arrayToString = objectToStringArray.join('\n');

      return arrayToString;
    },
    closeWithErrors() {
      this.isImported = false;

      this.form.data = this.getErrorsStringify();
    },
  },

  computed: {
    ...mapGetters({
      landlords: 'landlord/landlords/landlords',
      tenants: 'landlord/tenants/tenants',
      user: 'user/user/user',
    }),
  },

  mounted() {
    this.fetchAllData();
  },

  created() {
    this.resetValidators();
    this.createValidators();
  },
};
</script>

<style scoped lang="scss">
@import '~styles/app.scss';

.import-message {
  width: 100%;
}
.form__label.with_checkbox {
  margin-left: 0px;

  .form__fieldset__custom-checkbox {
    margin: 10px 10px 10px 15px!important;
  }
}

.btn--main.active {
  color: var(--white);
  background-color: var(--brand-orange);
}

.btn--disabled {
  color: var(--white);

  &:hover {
    color: var(--white);
    cursor: default;
  }

  .fa {
    font-size: 18px;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/users/ImportUsersForm.vue